import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Card,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Box,
  TextField
} from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import React, { useState, useEffect } from "react";
import { List } from "react-window";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import Appbar from "../../components/Appbar";
import Body from "../../components/Body";
import Drawer from "../../components/Drawer";
import Chip from "@mui/material/Chip";
import { useParams, useNavigate } from "react-router-dom";
import { rolePermissionAddApi } from "../../store/role-permission/addRolePermission";
import { rolePermissionEditApi } from "../../store/role-permission/editRolePermission";
import { rolePermissionDetailsApi, resetRolePermission } from "../../store/role-permission/detailRolePermission";
import { modulePermissionApi, resetModulePermission } from "../../store/role-permission/permissionList";
import { useTheme } from '@mui/material/styles';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const SEARCH_FIELD_HEIGHT = 64;
const VIRTUAL_MENU_OVERSCAN = 6;
const VIRTUAL_MENU_HEIGHT = ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP;

/**
 * Formats a raw permission codename for human-readable display.
 * Removes `:` and `_`, then applies sentence case.
 *
 * Examples:
 *   "courses:moderate"         → "Courses moderate"
 *   "ai_tools:send"            → "Ai tools send"
 *   "analytics_dashboard:create" → "Analytics dashboard create"
 *   "pbl:export"               → "Pbl export"
 */
function formatCodename(codename) {
  if (!codename) return '';
  const str = String(codename); // guard: IDs are numbers when label not yet loaded
  const readable = str
    .replace(/[_:]/g, ' ')  // replace _ and : with space
    .replace(/\s+/g, ' ')   // collapse multiple spaces
    .trim();
  // Sentence case: uppercase first letter only
  return readable.charAt(0).toUpperCase() + readable.slice(1).toLowerCase();
}

const PermissionMenuRow = ({ index, items, style }) => (
  <Box style={style}>
    {items[index]}
  </Box>
);

const VirtualizedMenuList = React.forwardRef(function VirtualizedMenuList(props, ref) {
  const { children, hasMore, loading, onLoadMore, searchValue, onSearchChange, sx, ...other } = props;
  const items = React.Children.toArray(children);
  const menuHeight = Math.min(VIRTUAL_MENU_HEIGHT, items.length * ITEM_HEIGHT + ITEM_PADDING_TOP);

  const handleRowsRendered = ({ stopIndex }) => {
    if (hasMore && !loading && stopIndex >= items.length - VIRTUAL_MENU_OVERSCAN) {
      onLoadMore?.();
    }
  };

  return (
    <Box
      ref={ref}
      role="listbox"
      {...other}
      sx={{
        ...sx,
        height: menuHeight + SEARCH_FIELD_HEIGHT,
        maxHeight: VIRTUAL_MENU_HEIGHT + SEARCH_FIELD_HEIGHT,
        p: 0,
      }}
    >
      <Box sx={{ height: SEARCH_FIELD_HEIGHT, p: 1, pb: 0 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search permissions"
          value={searchValue}
          onChange={(event) => onSearchChange?.(event.target.value)}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => {
            if (event.key !== "Escape") {
              event.stopPropagation();
            }
          }}
        />
      </Box>
      <List
        defaultHeight={menuHeight}
        onRowsRendered={handleRowsRendered}
        overscanCount={VIRTUAL_MENU_OVERSCAN}
        rowComponent={PermissionMenuRow}
        rowCount={items.length}
        rowHeight={ITEM_HEIGHT}
        rowProps={{ items }}
        style={{ height: menuHeight, width: "100%" }}
      />
    </Box>
  );
});

const getPermissionMenuProps = ({ hasMore, loading, onLoadMore, searchValue, onSearchChange }) => ({
  PaperProps: {
    style: {
      maxHeight: VIRTUAL_MENU_HEIGHT + SEARCH_FIELD_HEIGHT,
      width: 250,
      overflow: "hidden",
    },
  },
  MenuListProps: {
    component: VirtualizedMenuList,
    disablePadding: true,
    hasMore,
    loading,
    onLoadMore,
    searchValue,
    onSearchChange,
  },
});

function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

const getPermissionIds = (permissions) => {
  if (!Array.isArray(permissions)) {
    return [];
  }

  return permissions.map((permission) => (
    typeof permission === "object" ? permission?.id : permission
  )).filter(Boolean);
};

const schema = yup
  .object({
    name: yup.string().required("Please Provide Content Name").trim(),
    description: yup.string().required("Please Provide Content Description").trim(),
  })
  .required();

export default function EditRole() {
  const theme = useTheme();
  const rolePermissionEdit = useSelector((state) => state?.rolePermissionEdit);
  const rolePermissionAdd = useSelector((state) => state?.rolePermissionAdd);
  const { result = {} } = useSelector((state) => state?.rolePermissionDetails || {});
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    setValue
  } = useForm({ resolver: yupResolver(schema), defaultValues: {} });
  const {
    result: modulePermissionList,
    loading: modulePermissionLoading,
    next: modulePermissionNext,
    page: modulePermissionPage,
  } = useSelector((state) => state?.modulePermissionList);
  const params = useParams();
  const navigate = useNavigate();
  const [modulePermissionSearch, setModulePermissionSearch] = useState("");
  const [debouncedModulePermissionSearch, setDebouncedModulePermissionSearch] = useState("");
  const hasMoreModulePermissions = Boolean(modulePermissionNext);
  const permissionLabelsById = React.useMemo(() => {
    const permissions = [
      ...(Array.isArray(result?.permissions) ? result.permissions : []),
      ...modulePermissionList,
    ];

    return permissions.reduce((labels, permission) => {
      labels[permission.id] = permission.codename;
      return labels;
    }, {});
  }, [modulePermissionList, result?.permissions]);
  const modulePermissionMenuProps = React.useMemo(() => getPermissionMenuProps({
    hasMore: hasMoreModulePermissions,
    loading: modulePermissionLoading,
    onLoadMore: () => {
      if (hasMoreModulePermissions && !modulePermissionLoading) {
        dispatch(modulePermissionApi({
          page: modulePermissionPage + 1,
          search: debouncedModulePermissionSearch,
        }));
      }
    },
    searchValue: modulePermissionSearch,
    onSearchChange: setModulePermissionSearch,
  }), [
    dispatch,
    debouncedModulePermissionSearch,
    hasMoreModulePermissions,
    modulePermissionLoading,
    modulePermissionPage,
    modulePermissionSearch,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetRolePermission())
      dispatch(resetModulePermission())
    }
  }, [dispatch])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedModulePermissionSearch(modulePermissionSearch.trim());
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [modulePermissionSearch]);

  useEffect(() => {
    dispatch(modulePermissionApi({
      page: 1,
      search: debouncedModulePermissionSearch,
    }))
  }, [debouncedModulePermissionSearch, dispatch]);
  useEffect(() => {
    if (params.id) {
      dispatch(rolePermissionDetailsApi({ ID: params.id }));
    }
  }, [dispatch, params.id]);

  const handleUpdateForm = async (data) => {
    let newObj = {
      ...data, is_active: true
    };

    if (params.id) {
      newObj = { ...newObj, ID: params.id };
      await dispatch(rolePermissionEditApi(newObj));

      if (!rolePermissionEdit?.loading) {
        navigate("/role-management");

      }
    } else {
      await dispatch(rolePermissionAddApi(newObj));

      if (!rolePermissionAdd?.loading) {
        navigate("/role-management");
      }
    }

  };

  useEffect(() => {
    let defalutValues = {};
    defalutValues["name"] = result?.name;
    defalutValues["description"] = result?.description;
    defalutValues["permission_ids"] = getPermissionIds(result?.permission_ids || result?.permissions);
    reset(defalutValues);
  }, [result, reset]);

  return (
    <>
      <Appbar />
      <Drawer />
      <Body>
        <form onSubmit={handleSubmit(handleUpdateForm)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <Card>

                <Typography>Details</Typography>
                <Stack spacing={2} my={2}>
                  <Divider />


                  <>
                    <Stack direction="row" spacing={2}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel shrink>Name</InputLabel>
                        <OutlinedInput
                          {...register("name")}
                          label="name"
                          notched
                          id="outlined-adornment-email"
                          error={errors?.name?.message}
                          defaultValue={result?.name}
                        />
                        <FormHelperText error>
                          {errors?.name?.message}
                        </FormHelperText>
                      </FormControl>

                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel shrink>Description</InputLabel>
                        <OutlinedInput
                          {...register("description")}
                          label="Description"
                          notched
                          multiline
                          error={errors?.description?.message}
                          defaultValue={result?.description}
                        />
                        <FormHelperText error>
                          {errors?.description?.message}
                        </FormHelperText>
                      </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel id="module-permission-label" htmlFor="permission_ids" shrink>
                          Module Permission
                        </InputLabel>
                        <Controller
                          name="permission_ids"
                          control={control}
                          defaultValue={[]}
                          render={({ field }) => (
                            <Select
                              {...field}
                              multiple
                              labelId="module-permission-label"
                              label="Module Permission"
                              value={watch('permission_ids', []) || []}
                              disabled={modulePermissionLoading && modulePermissionList.length === 0}
                              onChange={(e) => {
                                const {
                                  target: { value },
                                } = e;
                                const selectedValue = typeof value === 'string' ? value.split(',') : value;
                                setValue('permission_ids', selectedValue);
                                field.onChange(selectedValue);
                              }}
                              input={<OutlinedInput id="permission_ids" label="Module Permission" notched />}
                              renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(selected || []).map((value) => (
                                    <Chip key={value} label={formatCodename(permissionLabelsById[value] || value)} />
                                  ))}
                                </Box>
                              )}
                              MenuProps={modulePermissionMenuProps}
                            >
                              {!modulePermissionLoading && modulePermissionList.length === 0 && (
                                <MenuItem disabled>
                                  No permissions found
                                </MenuItem>
                              )}
                              {modulePermissionLoading && modulePermissionList.length === 0 && (
                                <MenuItem disabled>
                                  Loading permissions...
                                </MenuItem>
                              )}
                              {modulePermissionList.map((obj) => (
                                <MenuItem
                                  key={obj.id}
                                  value={obj.id}
                                  style={getStyles(obj.id, watch('permission_ids', []) || [], theme)}
                                >
                                  {formatCodename(obj.codename)}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        <FormHelperText error>
                          {errors?.permission_ids?.message}
                        </FormHelperText>
                      </FormControl>
                    </Stack>
                  </>
                  {/* } */}
                </Stack>
              </Card>
              <Button
                type="button"
                sx={{ float: "right", mt: 2, ml: 2 }}
                variant="contained"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                sx={{ float: "right", mt: 2 }}
                variant="contained"
              >
                {params.id ? "Update" : "Create"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Body>
    </>
  );
}
